import { MapPin } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card, MicroLabel, cn } from './ui/primitives';

export interface QuoteFact {
	label: string;
	value: ReactNode;
	/** Renders the value muted — "Not specified", "—". */
	muted?: boolean;
}

/**
 * A titled list of label/value rows — purpose, deadline, description.
 *
 * Editing is one control in the card's own header, never one per row: the rows
 * are facts about a single thing, and a pencil against one of them says the
 * others are somebody else's to change. It also has to live HERE rather than in
 * the section heading above, because a section can hold more than one card —
 * this one sits beside the delivery address, which is edited on its own.
 */
export function QuoteFacts({
	title,
	facts,
	action,
}: {
	title: string;
	facts: QuoteFact[];
	/** Edit the whole card. Omitted on a read-only surface. */
	action?: ReactNode;
}) {
	return (
		<Card padded>
			<div className="mb-2 flex items-center justify-between gap-2">
				<MicroLabel>{title}</MicroLabel>
				{action}
			</div>
			{facts.map((fact, i) => (
				<div
					key={fact.label}
					className={cn(
						'flex items-baseline justify-between gap-3 py-2',
						i < facts.length - 1 && 'border-b border-line-1'
					)}
				>
					<span className="shrink-0 text-sm text-text-3">{fact.label}</span>
					<span
						className={cn(
							'text-right text-sm leading-relaxed',
							fact.muted ? 'text-text-4' : 'text-text-1'
						)}
					>
						{fact.value}
					</span>
				</div>
			))}
		</Card>
	);
}

/** Where it ships. Its own card because an address is read as a block. */
export function QuoteAddress({
	title = 'Delivery address',
	lines,
	action,
	emptyLabel = 'No address set',
}: {
	title?: string;
	lines: string[];
	/** Set or change it. Omitted on a read-only surface. */
	action?: ReactNode;
	emptyLabel?: string;
}) {
	return (
		<Card padded>
			<div className="mb-3 flex items-center justify-between gap-2">
				<MicroLabel>{title}</MicroLabel>
				{action}
			</div>
			{lines.length === 0 ? (
				<div className="text-sm text-text-4">{emptyLabel}</div>
			) : (
				<div className="flex gap-2">
					<MapPin size={14} className="mt-0.5 shrink-0 text-text-4" />
					<div className="text-base leading-relaxed text-text-2">
						{lines.map((line, i) => (
							<div key={`${line}-${i}`}>{line}</div>
						))}
					</div>
				</div>
			)}
		</Card>
	);
}
